import { Asset } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { useGetAssetText } from '@/hooks/api/assets/useGetAssetText';
import { ComparePageObjectModel } from '@/poms/comparePageObjectModel';
import { getAssetFileName } from '@/utils/assets';
import { Box, styled, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { MouseEvent, useMemo } from 'react';
import { SplitDiffView } from './SplitDiffView';
import { buildTextDiffRows, countTextDiffChanges, exceedsTextDiffLimit, TextDiffRow } from './textDiffRows';
import { UnifiedDiffView } from './UnifiedDiffView';
import { TextDiffLayout, useTextDiffLayout } from './useTextDiffLayout';

const DiffRoot = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 6,
  overflow: 'hidden',
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
}));

const DiffToolbar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  padding: theme.spacing(0.75, 1.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const LayoutButton = styled(ToggleButton)(({ theme }) => ({
  textTransform: 'none',
  padding: theme.spacing(0.25, 1.25),
}));

const AddedCount = styled('span')(({ theme }) => ({
  color: theme.palette.success.main,
  fontWeight: 600,
}));

const RemovedCount = styled('span')(({ theme }) => ({
  color: theme.palette.error.main,
  fontWeight: 600,
}));

const FileNames = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.action.hover,
  '&[data-layout="unified"]': { gridTemplateColumns: '1fr' },
}));

const FileName = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(0.75, 1.5),
  fontWeight: 600,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const DiffScroller = styled(Box)({
  flex: 1,
  overflow: 'auto',
});

const MessageBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
});

const TextDiffMessage = ({ message }: { message: string }) => (
  <MessageBox data-testid={ComparePageObjectModel.TextDiffMessage}>
    <BodyText color='text.secondary'>{message}</BodyText>
  </MessageBox>
);

const DiffSummary = ({ rows }: { rows: TextDiffRow[] }) => {
  const { added, removed } = countTextDiffChanges(rows);
  if (added === 0 && removed === 0) {
    return <BodyText color='text.secondary'>No differences</BodyText>;
  }
  return (
    <BodyText>
      <AddedCount>+{added}</AddedCount> <RemovedCount>−{removed}</RemovedCount>
    </BodyText>
  );
};

const TextDiffTable = ({
  leftAsset,
  rightAsset,
  leftText,
  rightText,
}: {
  leftAsset: Asset;
  rightAsset: Asset;
  leftText: string;
  rightText: string;
}) => {
  const rows = useMemo(() => buildTextDiffRows(leftText, rightText), [leftText, rightText]);
  const { layout, saveLayout } = useTextDiffLayout();
  const leftFileName = getAssetFileName(leftAsset);
  const rightFileName = getAssetFileName(rightAsset);

  const handleLayoutChange = (_event: MouseEvent<HTMLElement>, newLayout: TextDiffLayout | null) => {
    if (newLayout) {
      saveLayout(newLayout);
    }
  };

  if (!rows) {
    return <TextDiffMessage message='Too many differences to show here' />;
  }

  return (
    <DiffRoot data-testid={ComparePageObjectModel.TextDiff}>
      <DiffToolbar>
        <Box data-testid={ComparePageObjectModel.TextDiffSummary}>
          <DiffSummary rows={rows} />
        </Box>
        <ToggleButtonGroup value={layout} exclusive onChange={handleLayoutChange} size='small' aria-label='Diff layout'>
          <LayoutButton value='split'>Side by side</LayoutButton>
          <LayoutButton value='unified'>Unified</LayoutButton>
        </ToggleButtonGroup>
      </DiffToolbar>
      <FileNames data-layout={layout}>
        {layout === 'split' ? (
          <>
            <FileName variant='body2'>{leftFileName}</FileName>
            <FileName variant='body2'>{rightFileName}</FileName>
          </>
        ) : (
          <FileName variant='body2'>{`${leftFileName} → ${rightFileName}`}</FileName>
        )}
      </FileNames>
      <DiffScroller>
        {layout === 'split' ? <SplitDiffView rows={rows} /> : <UnifiedDiffView rows={rows} />}
      </DiffScroller>
    </DiffRoot>
  );
};

export const TextDiffViewer = ({ leftAsset, rightAsset }: { leftAsset: Asset; rightAsset: Asset }) => {
  const left = useGetAssetText({ asset: leftAsset });
  const right = useGetAssetText({ asset: rightAsset });

  if (left.error || right.error) {
    return <TextDiffMessage message='Unable to load file contents' />;
  }
  if (left.text === undefined || right.text === undefined) {
    return <TextDiffMessage message='Loading file contents...' />;
  }
  if (exceedsTextDiffLimit(left.text) || exceedsTextDiffLimit(right.text)) {
    return <TextDiffMessage message="Text files over 1 MB can't be compared" />;
  }

  return <TextDiffTable leftAsset={leftAsset} rightAsset={rightAsset} leftText={left.text} rightText={right.text} />;
};
